/**
 * 세션 — 백엔드가 쿠키를 소유하고, 프런트는 상태를 **묻기만** 한다.
 *
 * 구 Nitro 는 자체 비밀 없이 쿠키를 패스스루했다. BFF 가 사라지면 브라우저가 백엔드 쿠키를
 * 그대로 받으므로 중계 자체가 소멸한다(F-0 §1.2 S2). 남는 것은 "지금 로그인돼 있나"뿐이다.
 *
 * **dev 개방 대칭**(F-0 §1.3 M2): 백엔드 `auth_required=false` 면 `/auth/me` 가
 * `{authenticated:false, auth_required:false}` 를 준다. 이것을 "로그아웃"으로 읽으면 dev 에서
 * 로그인 화면에 갇힌다 — 구 Nitro 가 이때 `role:'admin'` 을 돌려주던 이유다. 같은 대칭을
 * 여기서 만든다: **인증이 꺼져 있으면 열린 것**으로 본다(관리자 화면도 보여야 개발이 된다).
 */
import { API } from '@/api/paths'
import { request, UnauthorizedError } from '@/api/client'

export interface SessionUser {
  user_id: string
  login_id: string
  role: 'admin' | 'member'
  status?: string
}

export interface Session {
  /** 화면을 볼 수 있는가(= 인증됐거나, 인증 자체가 꺼져 있거나). */
  authed: boolean
  /** 백엔드가 인증을 강제하는가. false 면 dev 개방. */
  authRequired: boolean
  user: SessionUser | null
  /** 관리자 화면 노출 여부 — dev 개방이면 true(백엔드 require_admin 도 개방이라 대칭). */
  isAdmin: boolean
}

interface MeResponse {
  authenticated?: boolean
  auth_required?: boolean
  user?: SessionUser
}

export const ANONYMOUS: Session = {
  authed: false,
  authRequired: true,
  user: null,
  isAdmin: false,
}

export async function fetchSession(): Promise<Session> {
  try {
    const me = await request<MeResponse>(API.me)
    if (me.authenticated && me.user) {
      return {
        authed: true,
        authRequired: true,
        user: me.user,
        isAdmin: me.user.role === 'admin',
      }
    }
    // 인증이 꺼진 상태(dev) — 백엔드 게이트도 열려 있으므로 화면을 막지 않는다.
    const open = me.auth_required === false
    return { authed: open, authRequired: !open, user: null, isAdmin: open }
  } catch (error) {
    if (error instanceof UnauthorizedError) return ANONYMOUS
    throw error
  }
}

export async function login(loginId: string, password: string): Promise<Session> {
  // 실패는 백엔드가 generic 401 로 준다(계정 열거 방지) — 프런트가 사유를 지어내지 않는다.
  await request(API.login, { method: 'POST', body: { login_id: loginId, password } })
  return await fetchSession()
}

export async function logout(): Promise<void> {
  await request(API.logout, { method: 'POST' })
}
